import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fxRate, positionSize } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { dirClass, dirLabel, fmtClock, fmtCountdown, fmtPct, fmtTime, formatMoney, titleCase, useNow } from '../lib/format';
import { OUTCOME_TONE, RESULT_COLOR, type AccountSnapshot, type Evaluation, type Instrument, type PlanView, type RiskCheckView } from '../lib/types';
import type { PlanDraft } from '../lib/types-setups';
import { Button, Card, cx, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';
import { PlanStrategyChecklist, saveStrategyChecklist, StrategyChecklistPicker, type StrategyAnswers } from '../components/library/StrategyChecklist';

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Checks({ checks }: { checks: RiskCheckView[] }) {
  return (
    <div className="flex flex-col">
      {checks.map((c) => (
        <div key={c.code} className="flex items-start gap-2.5 border-t border-[#1a1f27] py-2.5">
          <span className={cx('min-w-[78px] text-xs font-bold', RESULT_COLOR[c.result])}>{c.result === 'NOT_APPLICABLE' ? 'N/A' : c.result}</span>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold">{c.name}</span>
            <span className="text-xs text-sub">{c.message}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

const OUTCOME_TEXT = {
  PASS: 'Every check passes. Submitting approves this plan straight away.',
  ESCALATE: 'A check is over the auto-approve limit. Submitting sends this plan to the Risk Manager.',
  REJECT: 'This plan breaks a hard limit and cannot be approved as it is. Reduce the risk or fix the flagged check.',
};

export function PlanNew() {
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const accounts = me.data?.accounts ?? [];
  const [accountId, setAccountId] = useState('');
  const account = accounts.find((a) => a.id === accountId) ?? accounts[0];
  const [instrumentId, setInstrumentId] = useState('');
  const [direction, setDirection] = useState<'LONG' | 'SHORT'>('LONG');
  const [orderType, setOrderType] = useState('LIMIT');
  const [entry, setEntry] = useState('');
  const [stop, setStop] = useState('');
  const [tp1, setTp1] = useState('');
  const [tp2, setTp2] = useState('');
  const [riskPct, setRiskPct] = useState('1.00');
  const [size, setSize] = useState('');
  const [timeframe, setTimeframe] = useState('H1');
  const [rationale, setRationale] = useState('');
  const [strategy, setStrategy] = useState<StrategyAnswers | null>(null);

  // INT-STP-05: "Convert to plan" opens this screen pre-filled from the setup and linked to it
  const [params] = useSearchParams();
  const setupParam = params.get('setup');
  const draft = useQuery({ queryKey: ['plan-draft', setupParam], queryFn: () => api<PlanDraft>(`/setups/${setupParam}/plan-draft`), enabled: !!setupParam, staleTime: Infinity });
  const prefilled = useRef(false);
  useEffect(() => {
    const d = draft.data;
    if (!d || prefilled.current) return;
    prefilled.current = true;
    if (d.accountId) setAccountId(d.accountId);
    setInstrumentId(d.instrumentId);
    setDirection(d.direction);
    setOrderType(d.orderType);
    setEntry(d.plannedEntry);
    setStop(d.stopLoss ?? '');
    setTp1(d.takeProfits[0]?.price ?? '');
    setTp2(d.takeProfits[1]?.price ?? '');
    if (d.timeframe) setTimeframe(d.timeframe);
    setRationale(d.rationale);
  }, [draft.data]);
  const setupId = draft.data?.setupId ?? null;

  const { data: instruments } = useQuery({ queryKey: ['instruments', account?.market.id], queryFn: () => api<Instrument[]>(`/instruments?marketId=${account!.market.id}`), enabled: !!account });
  const { data: snap } = useQuery({ queryKey: ['account', account?.id], queryFn: () => api<AccountSnapshot>(`/accounts/${account!.id}`), enabled: !!account });
  const { data: fx } = useQuery({ queryKey: ['fx'], queryFn: () => api<{ rates: Record<string, string>; dates: Record<string, string> }>('/fx/rates') });
  const mySymbols = useMemo(() => new Set(me.data?.symbols.map((s) => s.id)), [me.data]);
  const list = useMemo(() => [...(instruments ?? [])].sort((a, b) => Number(mySymbols.has(b.id)) - Number(mySymbols.has(a.id)) || a.symbol.localeCompare(b.symbol)), [instruments, mySymbols]);
  const inst = list.find((i) => i.id === instrumentId) ?? list[0];

  // position-size calculator (INT-TRD-01): size from risk %, rounded down to the size step
  useEffect(() => {
    if (!inst || !snap || !entry || !stop || !riskPct) return;
    const rate = fxRate(inst.quoteCurrency, snap.currency, fx?.rates ?? {});
    if (!rate) return;
    const r = positionSize({ equity: snap.equity, riskPct, entry, stopLoss: stop, pointValue: inst.pointValue, fxRate: rate, sizeStep: inst.sizeStep, minSize: inst.minSize });
    if (r) setSize(r.size);
  }, [inst, snap, fx, entry, stop, riskPct]);

  const body = account && inst && Number(entry) > 0 && Number(size) > 0
    ? { accountId: account.id, instrumentId: inst.id, direction, orderType, plannedEntry: entry, stopLoss: stop || null, takeProfits: [tp1, tp2].filter((p) => Number(p) > 0).map((price, i, arr) => ({ price, sizePct: arr.length === 2 ? 50 : 100 })), size, timeframe, rationale: rationale || null, ...(setupId && inst.id === draft.data?.instrumentId ? { setupId } : {}) }
    : null;
  const debounced = useDebounced(body, 400);
  const preview = useQuery({ queryKey: ['preview', debounced], queryFn: () => api<Evaluation>('/plans/preview', { body: debounced }), enabled: !!debounced, placeholderData: (p) => p });

  const create = useMutation({
    mutationFn: async (submit: boolean) => {
      const plan = await api<PlanView>('/plans', { body });
      if (strategy) await saveStrategyChecklist(plan.id, strategy);
      if (submit) await api(`/plans/${plan.id}/submit`, { method: 'POST' });
      return plan;
    },
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['today'] });
      void qc.invalidateQueries({ queryKey: ['setups'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      nav(`/plans/${p.id}`);
    },
  });

  if (!me.data) return null;
  if (setupParam && draft.error) return <ErrorText error={errorText(draft.error)} />;
  if (!accounts.length) return <Card className="p-5 text-sub">You have no trading accounts yet.</Card>;
  const ev = preview.data;
  const tone = ev ? OUTCOME_TONE[ev.outcome] : 'neutral';

  return (
    <>
      <PageHeader
        title={`New trade plan${inst ? ` · ${inst.symbol} ${direction === 'LONG' ? 'long' : 'short'}` : ''}`}
        sub={snap ? `Account ${snap.code} · equity ${formatMoney(snap.equity, snap.currency)}` : ''}
        back={<Link to="/" className="text-[13px]">← Today</Link>}
        right={
          <>
            <Button onClick={() => create.mutate(false)} disabled={!body || create.isPending}>
              Save draft
            </Button>
            <Button variant="primary" onClick={() => create.mutate(true)} disabled={!body || create.isPending || ev?.outcome === 'REJECT'}>
              {ev?.outcome === 'PASS' ? 'Submit (auto-approve)' : ev?.outcome === 'REJECT' ? 'Fix to submit' : 'Submit for approval'}
            </Button>
          </>
        }
      />
      <ErrorText error={create.error ? errorText(create.error) : null} />
      {draft.data && (
        <Card tone="accent" className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <span className="text-ink2">
            {inst?.id === draft.data.instrumentId ? (
              <>
                Pre-filled from your <strong>{draft.data.setup.symbol} {draft.data.setup.direction === 'LONG' ? 'long' : 'short'}</strong> setup ({draft.data.setup.timeframe}): entry at the zone, stop at the invalidation level. Saving this plan marks the setup <strong>Converted</strong>.
              </>
            ) : (
              <>The symbol no longer matches the {draft.data.setup.symbol} setup, so this plan won't be linked to it.</>
            )}
          </span>
          <Link to={`/setups/${draft.data.setupId}`} className="text-sm font-semibold">
            View setup →
          </Link>
        </Card>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_480px] flex-col gap-3.5">
          <Card className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
            <Field label="Account">
              <Select value={account?.id} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} · {a.market.code}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Symbol">
              <Select value={inst?.id} onChange={(e) => setInstrumentId(e.target.value)}>
                {list.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.symbol}
                    {mySymbols.has(i.id) ? '' : ' (not assigned)'}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Order type">
              <Select value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                <option value="MARKET">Market</option>
                <option value="LIMIT">Limit</option>
                <option value="STOP">Stop</option>
              </Select>
            </Field>
            <Field label="Timeframe">
              <Input value={timeframe} onChange={(e) => setTimeframe(e.target.value)} />
            </Field>
            <div className="col-span-2 flex flex-col gap-1.5 md:col-span-4">
              <span className="text-xs text-muted">Direction</span>
              <Segmented
                label="Direction"
                options={[
                  { value: 'LONG', label: '▲ Long' },
                  { value: 'SHORT', label: '▼ Short' },
                ]}
                value={direction}
                onChange={setDirection}
                colors={{ LONG: '#3dd68c', SHORT: '#ff7a6e' }}
              />
            </div>
            <Field label="Entry">
              <Input mono inputMode="decimal" value={entry} onChange={(e) => setEntry(e.target.value)} />
            </Field>
            <Field label="Stop loss">
              <Input mono inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} className="text-down" />
            </Field>
            <Field label="Target 1">
              <Input mono inputMode="decimal" value={tp1} onChange={(e) => setTp1(e.target.value)} className="text-up" />
            </Field>
            <Field label="Target 2 (optional)">
              <Input mono inputMode="decimal" value={tp2} onChange={(e) => setTp2(e.target.value)} className="text-up" />
            </Field>
          </Card>

          <Card className="flex flex-col gap-3.5 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Position size</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Field label="Risk % of equity" hint={snap ? `auto ≤ ${fmtPct(snap.policy.autoMaxRiskPct)} · max ${fmtPct(snap.policy.hardMaxRiskPct)}` : undefined}>
                <Input mono inputMode="decimal" value={riskPct} onChange={(e) => setRiskPct(e.target.value)} />
              </Field>
              <Field label={`Size (${inst?.sizeUnit.toLowerCase() ?? ''})`} hint="calculated, editable">
                <Input mono inputMode="decimal" value={size} onChange={(e) => setSize(e.target.value)} />
              </Field>
              <div className="flex flex-col gap-1 rounded-lg bg-raised p-3">
                <span className="text-xs text-muted">Risk amount</span>
                <span className="font-mono text-base font-semibold">{ev?.riskAmount && snap ? formatMoney(ev.riskAmount, snap.currency) : '—'}</span>
              </div>
              <div className="flex flex-col gap-1 rounded-lg bg-raised p-3">
                <span className="text-xs text-muted">R : R to T1 / T2</span>
                <span className="font-mono text-base font-semibold">{ev?.rewardRisk.length ? ev.rewardRisk.join(' / ') : '—'}</span>
              </div>
            </div>
          </Card>

          <StrategyChecklistPicker marketCode={account?.market.code} value={strategy} onChange={setStrategy} />

          <Card className="p-4">
            <Field label="Rationale">
              <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Why this trade, why now, what invalidates it" />
            </Field>
          </Card>
        </section>

        <aside aria-label="Risk check" className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5">
          <Card tone={ev?.outcome === 'PASS' ? 'up' : ev?.outcome === 'ESCALATE' ? 'warn' : ev?.outcome === 'REJECT' ? 'down' : undefined} className="flex flex-col gap-3 p-4">
            <div className="flex items-center justify-between">
              <h2 className="m-0 text-[15px] font-semibold">Automatic risk check</h2>
              {ev && <Pill tone={tone}>{ev.outcome === 'PASS' ? 'AUTO-APPROVE' : ev.outcome}</Pill>}
            </div>
            {!ev && <span className="text-sub">Enter entry, stop and size to see the check.</span>}
            {preview.error && <ErrorText error={errorText(preview.error)} />}
            {ev && (
              <>
                <span className="leading-relaxed text-ink2">
                  {OUTCOME_TEXT[ev.outcome]} {ev.riskPct && <strong>Risk {fmtPct(ev.riskPct)}.</strong>}
                </span>
                <Checks checks={ev.checks} />
              </>
            )}
          </Card>
        </aside>
      </div>
    </>
  );
}

export function PlanDetail() {
  const { id } = useParams();
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const now = useNow(1000);
  const tz = me.data?.timezone ?? 'UTC';
  const { data: p, error } = useQuery({ queryKey: ['plan', id], queryFn: () => api<PlanView>(`/plans/${id}`), refetchInterval: 15_000 });
  const [price, setPrice] = useState('');
  const [size, setSize] = useState('');
  useEffect(() => {
    if (p) {
      setPrice((v) => v || p.plannedEntry);
      setSize((v) => v || p.size);
    }
  }, [p]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['plan', id] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  const submit = useMutation({ mutationFn: () => api(`/plans/${id}/submit`, { method: 'POST' }), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => api(`/plans/${id}/cancel`, { method: 'POST' }), onSuccess: refresh });
  const execute = useMutation({
    mutationFn: () => api<{ tradeId: string }>(`/plans/${id}/execute`, { body: { price, size } }),
    onSuccess: (r) => {
      refresh();
      nav(`/trades/${r.tradeId}`);
    },
  });

  if (error) return <ErrorText error={errorText(error)} />;
  if (!p) return null;
  const mine = p.trader.id === me.data?.id;
  const fromSetup = (p as PlanView & { setupId?: string | null }).setupId ?? null;
  const approved = p.status === 'AUTO_APPROVED' || p.status === 'APPROVED';
  const pending = p.approvals.find((a) => a.status === 'PENDING');
  const statusTone = approved ? 'up' : p.status === 'PENDING_APPROVAL' ? 'warn' : p.status === 'REJECTED' || p.status === 'EXPIRED' ? 'down' : 'neutral';

  return (
    <>
      <PageHeader
        title={
          <>
            {p.instrument.symbol} <span className={dirClass(p.direction)}>{dirLabel(p.direction)}</span> <span className="text-base font-normal text-muted">· {p.account.code}</span>
          </>
        }
        sub={`${titleCase(p.orderType)} at ${p.plannedEntry} · stop ${p.stopLoss ?? '—'} · targets ${p.takeProfits.map((t) => t.price).join(' / ') || '—'} · size ${p.size}`}
        back={<Link to="/" className="text-[13px]">← Today</Link>}
        right={<Pill tone={statusTone}>{titleCase(p.status)}</Pill>}
      />
      <ErrorText error={[submit.error, cancel.error, execute.error].filter(Boolean).map(errorText)[0] ?? null} />
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_480px] flex-col gap-3.5">
          {p.status === 'DRAFT' && mine && (
            <Card className="flex items-center justify-between gap-3 p-4">
              <span className="text-sub">This plan is a draft. Submitting runs the risk check.</span>
              <Button variant="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
                Submit
              </Button>
            </Card>
          )}
          {pending && (
            <Card tone="warn" className="flex flex-col gap-2 p-4">
              <span className="font-semibold">
                Waiting for the {titleCase(pending.role)} (level {pending.level})
              </span>
              <span className="text-sub">
                Answer due {fmtTime(pending.slaDueAt, tz)} · <span className="font-mono text-warn">{fmtClock((new Date(pending.slaDueAt).getTime() - now) / 1000)}</span> left, then it moves up a level.
              </span>
            </Card>
          )}
          {approved && mine && (
            <Card tone="up" className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <h2 className="m-0 text-[15px] font-semibold">Record your entry</h2>
                {p.approvalExpiresAt && <span className="text-sm text-sub">Approval expires {fmtCountdown(p.approvalExpiresAt, now)}</span>}
              </div>
              <p className="m-0 text-sm text-sub">VTF doesn't place orders. Enter what was filled at your broker.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fill price">
                  <Input mono inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
                </Field>
                <Field label="Filled size">
                  <Input mono inputMode="decimal" value={size} onChange={(e) => setSize(e.target.value)} />
                </Field>
              </div>
              <Button variant="success" className="self-start" onClick={() => execute.mutate()} disabled={execute.isPending}>
                {execute.isPending ? 'Recording…' : 'Record entry · open trade'}
              </Button>
            </Card>
          )}
          {p.trade && (
            <Card className="p-4">
              <Link to={`/trades/${p.trade.id}`} className="font-semibold">
                Open the trade →
              </Link>
            </Card>
          )}
          {fromSetup && (
            <Card className="flex items-center justify-between gap-3 px-4 py-3">
              <span className="text-sub">This plan comes from a marked setup.</span>
              <Link to={`/setups/${fromSetup}`} className="font-semibold">
                View setup →
              </Link>
            </Card>
          )}
          <Card className="flex flex-col gap-2 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Approval trail</h2>
            {!p.approvals.length && <span className="text-sub">{p.status === 'AUTO_APPROVED' ? 'Auto-approved: every check passed.' : 'No approvals yet.'}</span>}
            {p.approvals.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-lg bg-raised px-3 py-2.5">
                <div className="flex flex-col">
                  <span className="font-semibold">
                    Level {a.level} · {titleCase(a.role)}
                  </span>
                  <span className="text-xs text-muted">
                    {a.decidedBy ? `${a.decidedBy.name} · ` : ''}
                    {a.reason ?? ''}
                  </span>
                </div>
                <span className="text-xs font-semibold text-sub">{titleCase(a.status)}</span>
              </div>
            ))}
            {p.rationale && <p className="m-0 mt-2 leading-relaxed text-ink2">{p.rationale}</p>}
            {mine && ['DRAFT', 'PENDING_APPROVAL', 'AUTO_APPROVED', 'APPROVED'].includes(p.status) && (
              <Button variant="danger" className="mt-2 self-start" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                Cancel plan
              </Button>
            )}
          </Card>
        </section>
        <aside className="flex min-w-0 flex-[1_1_340px] flex-col">
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex items-center justify-between">
              <h2 className="m-0 text-[15px] font-semibold">Risk check</h2>
              {p.riskCheck && <Pill tone={OUTCOME_TONE[p.riskCheck.outcome as keyof typeof OUTCOME_TONE]}>{p.riskCheck.outcome}</Pill>}
            </div>
            <span className="text-sub">
              Risk {fmtPct(p.riskPct)} · {p.riskAmount ? formatMoney(p.riskAmount, p.account.currency) : '—'} · R:R {p.rewardRisk ?? '—'}
            </span>
            {p.riskCheck ? <Checks checks={p.riskCheck.checks} /> : <span className="text-sub">Runs when the plan is submitted.</span>}
          </Card>
          <PlanStrategyChecklist planId={p.id} />
        </aside>
      </div>
    </>
  );
}
